import { useState } from "react"
import { useLocalization } from "@fluent/react"
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material"
import { APIError, unknownError } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { RichTextEditor } from "../../../app/RichTextEditor"
import { updateOperationalDashboardAreaAgendaSummary } from "../operationalDashboards"

export const OperationalDashboardAgendaSummaryDialog = ({
    dashboardId, areaId, areaName, summary, onClose, onSaved,
}: {
    dashboardId: number
    areaId: number
    areaName: string
    summary: string | null
    onClose: () => void
    onSaved: () => void
}) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const [value, setValue] = useState(summary ?? "")
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState<APIError | null>(null)
    const hasContent = Boolean(new DOMParser().parseFromString(value, "text/html").body.textContent?.trim())

    const handleSave = async () => {
        const updatedSummary = value.trim()
        if (!hasContent || updatedSummary === (summary ?? "").trim() || saving) return
        setSaving(true)
        setServerError(null)
        const result = await dispatch(updateOperationalDashboardAreaAgendaSummary({
            dashboardId, areaId, dto: { summary: updatedSummary },
        }))
        if (updateOperationalDashboardAreaAgendaSummary.fulfilled.match(result) && result.payload === true) {
            onSaved()
        } else {
            setServerError(updateOperationalDashboardAreaAgendaSummary.rejected.match(result)
                ? result.payload ?? unknownError() : unknownError())
            setSaving(false)
        }
    }

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md"
            aria-labelledby="operational-dashboard-agenda-summary-title">
            <DialogTitle id="operational-dashboard-agenda-summary-title"
                sx={{ bgcolor: "grey.200", color: "text.primary", fontWeight: 700 }}>
                {label("decision-action", "Decyzja / akcja")}
                <Typography variant="body2" color="text.secondary">{areaName}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                {serverError && (
                    <Alert severity="error" onClose={() => setServerError(null)} sx={{ mb: 2 }}>
                        {serverError.kind === "http" && serverError.problem?.title
                            ? l10n.getString(serverError.problem.title, null,
                                `_${serverError.problem.detail || "Nie udało się zapisać decyzji."}`)
                            : label("agenda-summary-save-error", "Nie udało się zapisać decyzji.")}
                    </Alert>
                )}
                <Box sx={{ pt: 1 }}>
                    <RichTextEditor label={label("decision-action", "Decyzja / akcja")}
                        value={value} onValueChange={setValue} editable={!saving} />
                </Box>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} disabled={!hasContent || value.trim() === (summary ?? "").trim()}
                    onClick={handleSave} />
                <CancelButton loading={saving} disabled={saving} onClick={onClose} />
            </DialogActions>
        </Dialog>
    )
}
